import { Box, Button, Divider, Skeleton, Typography } from "@mui/material";
import { ModalV1 } from "components/modals/ModalV1";
import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import { auth } from "service";
import { queryGetAssignmentHistory } from "service/api";
import { formatDateToString } from "service/utils";

export const Message = ({
  displayName,
  markdownText,
  sentAtTimestampISO,
  isMyMessage,
  loading,
}: {
  isMyMessage?: boolean;
  displayName: string;
  markdownText: string;
  sentAtTimestampISO: string;
  loading?: boolean;
}) => {
  return (
    <Box
      sx={{
        background: isMyMessage ? "#d1e9ff" : "#f1f1f1",
        borderRadius: "8px",
        width: "100%",
        maxWidth: "480px",
        marginLeft: isMyMessage ? "auto" : "0",
      }}
    >
      <Box
        sx={{
          padding: "12px 12px 4px",
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
        }}
      >
        <Typography fontWeight={"600"}>
          {loading ? (
            <Skeleton height={"24px"} />
          ) : isMyMessage ? (
            displayName + " (Вы)"
          ) : (
            displayName
          )}
        </Typography>
        <Typography variant="caption">
          {loading ? (
            <Skeleton />
          ) : (
            formatDateToString(new Date(sentAtTimestampISO))
          )}
        </Typography>
      </Box>
      <Divider />
      <Box padding={"4px 12px"}>
        {loading ? (
          <Skeleton height={"116px"} variant="rounded" />
        ) : (
          <Typography>
            <Markdown>{markdownText}</Markdown>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const ModalAssignmentHistory = ({
  open,
  handleClose,
  assignmentId,
}: {
  open: boolean;
  handleClose: () => void;
  assignmentId: number;
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([{}, {}]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const res = await queryGetAssignmentHistory({ assignmentId });
      setHistory(res?.data.messages ?? []);
    } catch (error: any) {
      console.error(error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadHistory();
  }, [open]);

  useEffect(() => {
    scrollToBlockRef.current?.scrollIntoView({
      block: "end",
    });
  }, [history, loading]);

  const scrollToBlockRef = useRef<HTMLElement>(null);
  return (
    <ModalV1
      open={open}
      handleClose={handleClose}
      title={"История"}
      width="768px"
      children={
        <Box ref={scrollToBlockRef}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              flex: 1,
              overflow: "auto",
              minHeight: "320px",
              position: "relative",
            }}
          >
            {!loading && history.length == 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                  textAlign: "center",
                }}
              >
                <Typography fontWeight={"600"} fontSize={"18px"}>
                  Нет сообщений
                </Typography>
              </Box>
            )}
            {history.map((h, i) => {
              const isMyMessage = h?.user_id
                ? h.user_id == auth.GetUser()?.uid
                : i % 2 != 1;
              return (
                <Message
                  displayName={
                    loading ? "" : h?.user_display_name || h.user_email
                  }
                  markdownText={loading ? "" : h.body}
                  sentAtTimestampISO={loading ? "" : h.sent_at}
                  isMyMessage={isMyMessage}
                  loading={loading}
                  key={i}
                />
              );
            })}
          </Box>
          {
            <Box
              sx={{
                marginTop: "24px",
                display: "flex",
                position: "sticky",
                bottom: "0",
              }}
            >
              <Button
                sx={{
                  ml: "auto",
                }}
                variant="contained"
                disabled={loading}
                onClick={loadHistory}
              >
                Обновить
              </Button>
            </Box>
          }
        </Box>
      }
    />
  );
};
